"use client";
import { GET_PRODUCT_APPLICATION } from "@/lib/constants";
import {
  PERSONAL_LOAN_STEPS,
  PRODUCT_HANDLER,
  STAGE,
  SUB_PRODUCTS,
} from "@/lib/constants/Texts";
import useProgressHook from "@/lib/hooks/progressHook";
import { useGetApplicationQuery } from "@/lib/redux";
import { checkForRpl, REPEAT_PERSONAL_LOAN_STEPS, stageMapRPL } from "@/lib/utils/rpl.utils";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import Step from "@mui/material/Step";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { styled } from "@mui/material/styles";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`& .${stepConnectorClasses.line}`]: {
    border: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:after": {
      content: '" > "',
      color: theme.palette.text.secondary,
      fontWeight: "bold",
      fontSize: 16,
    },
  },
}));

const stageMapPL = {
  [STAGE.LOAN_AMOUNT]: "Personal Details",
  [STAGE.PAN]: "Personal Details",
  [STAGE.CKYC]: "Personal Details",
  [STAGE.ADD_ADDRESS]: "Personal Details",
  [STAGE.PERSONAL_DETAILS]: "Personal Details",
  [STAGE.OFFERS]: "Offers",
  [STAGE.DIGI_KYC]: "KYC Verification",
  [STAGE.EKYC]: "KYC Verification",
  [STAGE.WORK_DETAILS]: "Work Details",
  [STAGE.MANDATE]: "Re-Payment Setup",
  [STAGE.MANDATE_PENNYDROP]: "Re-Payment Setup",
  [STAGE.E_SIGN]: "E-Sign",
  [STAGE.E_SIGN_KFS_INCOMPLETE]: "E-Sign",
  [STAGE.BRE_CPV_TVR]: "",
  [STAGE.LMS_ONBOARDING]: "",
};


export const LinearProgressBar = ({ value: progressValue }) => {
  const GradientLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 18,
    borderRadius: 50,
    position: "relative",
    [`& .MuiLinearProgress-bar`]: {
      background: "linear-gradient(90deg, #004135 0%, #1A9C84 100%)",
      borderRadius: 50,
    },
    background: theme.palette.grey[200],
  }));
  return (<Box sx={{ position: "relative", width: "100%" }}>
    <GradientLinearProgress variant="determinate" value={progressValue} />
    <Box
      sx={{
        position: "absolute",
        top: 0,
        right: 5,
        left:
          progressValue <= 30
            ? `calc(${progressValue}% - 42px)`
            : `calc(${progressValue}% - 50px)`, // 24px is half the width of the label box for centering
        height: "100%",
        display: "flex",
        alignItems: "center",
        pointerEvents: "none",
        userSelect: "none",
        transition: "left 0.3s cubic-bezier(0.4,0,0.2,1)",
      }}
    >
      <Typography
        variant="body2"
        align="center"
        sx={{
          fontWeight: 600,
          color: "#fff",
          px: 1.5,
          borderRadius: 2,
          minWidth: 40,
          textAlign: "center",
          fontSize: 12,
        }}
      >
        {`${Math.round(progressValue)}%`}
      </Typography>
    </Box>
  </Box>)
}

function Progress({ size = "4rem", product, isStepperRequired = true }) {
  const t = useTranslations("Progress");
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const pathname = usePathname();

  const { data: applicationData } = useGetApplicationQuery("", {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data
  );
  const isRPL = checkForRpl(productApplication);
  const stageMap = isRPL ? stageMapRPL : stageMapPL;
  const loanStepsList = isRPL ? REPEAT_PERSONAL_LOAN_STEPS : PERSONAL_LOAN_STEPS;

  const { activeStage, progressValue } = useProgressHook(
    productApplication?.stage, isRPL
  );

  const routeTitleMap = [
    { match: "insurance-protection-plan", label: "Hero Suraksha plan" },
  ];
  const derivedRouteTitle =
    routeTitleMap.find((r) => pathname.includes(r.match))?.label ??
    stageMap[productApplication?.stage];
    
  return (
    <Stack gap={1} mb={2}>
      {!isBelowMd && isStepperRequired ? (
        <Stepper
          alternativeLabel={isBelowMd}
          activeStep={activeStage}
          connector={<CustomConnector />}
        >
          {loanStepsList?.map((label, idx) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      ) : (
        <Typography fontWeight={500} fontSize={{ xs: 14, sm: 18 }}>
          {derivedRouteTitle}
        </Typography>
      )}
      <LinearProgressBar value={progressValue} />
    </Stack>
  );
}

export default Progress;
